import React, { useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { PricingTableFormValues } from "@/schemas/pricingTableSchema";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { Trash2, Plus, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DocumentType, isPlatformDocumentType } from '@/models';

interface PricingTableCustomFormFeesProps {
  documentTypes: DocumentType[];
  feesApportionParties?: any[];
}

export const PricingTableCustomFormFees: React.FC<PricingTableCustomFormFeesProps> = ({
  documentTypes,
  feesApportionParties = [],
}) => {
  const { control, setValue, getValues, watch } = useFormContext<PricingTableFormValues>();
  const { fields: customFormFeeFields, append: appendCustomFormFee, remove: removeCustomFormFee } = useFieldArray({
    control,
    name: "custom_form_fees",
  });



  // Filter document types that allow requestor upload (custom forms)
  const documentTypesWithCustomForms = documentTypes.filter(
    docType => isPlatformDocumentType(docType) && docType.accept_custom_form === true
  );

  const handleAddCustomFormFee = () => {
    const availableDocTypes = documentTypesWithCustomForms.filter(docType => {
      const currentConfigs = getValues('custom_form_fees') || [];
      return !currentConfigs.some(config => config.document_type_id === docType.id);
    });

    if (availableDocTypes.length === 0) {
      toast.error("All document types with custom form upload already have fee configurations");
      return;
    }

    const firstDocType = availableDocTypes[0];
    
    appendCustomFormFee({
      document_type_id: firstDocType.id,
      document_type_name: firstDocType.name,
      accept_custom_form: isPlatformDocumentType(firstDocType) ? firstDocType.accept_custom_form : false,
      amount: 0,
      apportionment: (feesApportionParties || []).map(party => ({
        apportionment_party_id: party.id,
        amount: 0
      }))
    });
  };

  const getAvailableDocumentTypes = (currentDocTypeId?: number) => {
    const currentConfigs = getValues('custom_form_fees') || [];
    return documentTypesWithCustomForms.filter(docType => {
      // Include current selection or types not already configured
      return docType.id === currentDocTypeId || 
        !currentConfigs.some(config => config.document_type_id === docType.id);
    });
  };

  const updateApportionmentAmount = (customFormIndex: number, partyId: number, value: string) => {
    const numericValue = parseFloat(value) || 0;
    const currentApportionments = watch(`custom_form_fees.${customFormIndex}.apportionment`) || [];
    
    const existingIndex = currentApportionments.findIndex(a => a.apportionment_party_id === partyId);
    
    let newApportionment;
    if (existingIndex >= 0) {
      // Update existing apportionment
      newApportionment = currentApportionments.map((a, index) => 
        index === existingIndex ? { ...a, amount: numericValue } : a
      );
    } else {
      // Add new apportionment
      newApportionment = [...currentApportionments, { apportionment_party_id: partyId, amount: numericValue }];
    }
    
    setValue(`custom_form_fees.${customFormIndex}.apportionment`, newApportionment);
    
    // Validate total
    setTimeout(() => {
      validateRowTotal(customFormIndex);
    }, 100);
  };

  const validateRowTotal = (customFormIndex: number) => {
    const apportionments = watch(`custom_form_fees.${customFormIndex}.apportionment`) || [];
    const feeAmount = watch(`custom_form_fees.${customFormIndex}.amount`) || 0;
    const total = apportionments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    
    if (Math.abs(total - feeAmount) > 0.01) {
      toast.warning(`Custom form fee apportionments total ($${total.toFixed(2)}) should equal the fee amount of $${feeAmount.toFixed(2)}`);
    }
  };

  const isApportionmentValid = (customFormIndex: number) => {
    const apportionments = watch(`custom_form_fees.${customFormIndex}.apportionment`) || [];
    const feeAmount = watch(`custom_form_fees.${customFormIndex}.amount`) || 0;
    const total = apportionments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    
    return Math.abs(total - feeAmount) <= 0.01;
  };

  const getApportionmentAmount = (apportionments: any[] = [], partyId: number) => {
    const apportionment = apportionments.find(a => a.apportionment_party_id === partyId);
    return apportionment ? apportionment.amount || 0 : 0;
  };

  if (documentTypesWithCustomForms.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Upload className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium">No Custom Form Document Types</p>
        <p className="text-sm">
          No document types are configured to allow requestor upload (custom forms).
          Configure document types to allow custom form upload to set fees here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Custom Form Fees</h3>
          <p className="text-sm text-muted-foreground">
            Configure fees for document types that allow requestor upload (custom forms)
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleAddCustomFormFee}
          disabled={customFormFeeFields.length >= documentTypesWithCustomForms.length}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Custom Form Fee
        </Button>
      </div>

      {customFormFeeFields.length === 0 ? (
        <div className="text-center p-6 border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground">No custom form fees configured yet.</p>
          <p className="text-sm text-muted-foreground mt-1">
            Click "Add Custom Form Fee" to get started.
          </p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document Type</TableHead>
                <TableHead>Fee Amount</TableHead>
                {(feesApportionParties || []).map(party => (
                  <TableHead key={party.id}>
                    {party.description || party.entity_name}
                  </TableHead>
                ))}
                <TableHead className="w-16">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customFormFeeFields.map((customFormField, customFormIndex) => {
                const currentApportionments = watch(`custom_form_fees.${customFormIndex}.apportionment`) || [];
                
                return (
                  <TableRow key={customFormField.id}>
                    <TableCell>
                      <FormField
                        control={control}
                        name={`custom_form_fees.${customFormIndex}.document_type_id`}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <select
                                value={field.value?.toString() || ''}
                                onChange={(e) => {
                                  const documentTypeId = parseInt(e.target.value);
                                  const selectedDocType = getAvailableDocumentTypes(documentTypeId)
                                    .find(dt => dt.id === documentTypeId);
                                  
                                  field.onChange(documentTypeId);
                                  setValue(`custom_form_fees.${customFormIndex}.document_type_name`, selectedDocType?.name);
                                        setValue(`custom_form_fees.${customFormIndex}.accept_custom_form`,
        selectedDocType && isPlatformDocumentType(selectedDocType) ? selectedDocType.accept_custom_form : false);
                                }}
                                className="w-full px-3 py-2 border border-input rounded-md bg-background"
                              >
                                <option value="">Select document type</option>
                                {getAvailableDocumentTypes(getValues(`custom_form_fees.${customFormIndex}.document_type_id`)).map((docType) => (
                                  <option key={docType.id} value={docType.id.toString()}>
                                    {docType.name}
                                  </option>
                                ))}
                              </select>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <FormField
                        control={control}
                        name={`custom_form_fees.${customFormIndex}.amount`}
                        render={({ field }) => {
                          const isValid = isApportionmentValid(customFormIndex);
                          return (
                            <FormItem>
                              <FormControl>
                                <div className="flex items-center space-x-1">
                                  <span className="text-gray-500">$</span>
                                  <Input 
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                    className={`amount-input ${!isValid ? 'border-orange-500' : ''}`}
                                    style={{ appearance: "textfield" }}
                                    value={field.value === undefined || field.value === null ? 0 : field.value}
                                    onChange={(e) => {
                                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                      field.onChange(isNaN(value) ? 0 : value);
                                      // Validate total after amount change
                                      setTimeout(() => validateRowTotal(customFormIndex), 100);
                                    }}
                                  />
                                  {!isValid && (
                                    <div className="flex items-center" title="Apportionment total doesn't match fee amount">
                                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                                    </div>
                                  )}
                                </div>
                              </FormControl>
                            </FormItem>
                          );
                        }}
                      />
                    </TableCell>
                    
                    {(feesApportionParties || []).map(party => (
                      <TableCell key={party.id}>
                        <div className="flex items-center space-x-1">
                          <span className="text-gray-500">$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={getApportionmentAmount(currentApportionments, party.id)}
                            onChange={(e) => updateApportionmentAmount(customFormIndex, party.id, e.target.value)}
                            className="w-20"
                          />
                        </div>
                      </TableCell>
                    ))}
                    
                    <TableCell>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeCustomFormFee(customFormIndex)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default PricingTableCustomFormFees; 